// Pure helpers for managed monitoring cards backed by live n8n workflows or parent Hermes skills.
import type {
  CatalogAgentView,
  CreateManagedHermesAgentInput,
  CreateManagedN8nAgentInput,
  LoopView,
  SkillSummaryView,
} from '../../../apps/apis/commandCenter/types';

const ID_MAX = 64;
const TEXT_MAX = 512;
const N8N_LIVE_PREFIX = 'n8n:wf:';

export interface N8nWorkflowOption {
  workflowId: string;
  name: string;
  cardId: string;
  onBoard: boolean;
}

export interface HermesSkillOption {
  skillId: string;
  name: string;
  /** Where the skill came from: the origin node id for agent-created skills, 'admin' otherwise. */
  origin: string;
  /** Newest catalog version; the card endpoint validates that this exact version exists. */
  version: string;
  /** One-line summary from the skill's SKILL.md; null when it declares none. */
  description: string | null;
  cardId: string;
  /** True when the board already carries a card for this skill. */
  onBoard: boolean;
}

/** Coerces any string into the backend's id pattern (^[A-Za-z0-9._-]+$) and length cap. */
export function sanitizeAgentId(raw: string): string {
  return raw
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, ID_MAX);
}

/** Live n8n workflows -> pickable monitoring cards, preserving the workflow id used by execution joins. */
export function n8nWorkflowOptions(n8nAgents: CatalogAgentView[], existingCardIds: Set<string>): N8nWorkflowOption[] {
  return n8nAgents
    .filter((agent) => agent.id.startsWith(N8N_LIVE_PREFIX))
    .map((agent) => {
      const workflowId = agent.id.slice(N8N_LIVE_PREFIX.length);
      const cardId = `n8n-${workflowId}`;
      return { workflowId, name: agent.name, cardId, onBoard: existingCardIds.has(cardId) };
    });
}

/** Narrow n8n workflows by a case-insensitive name or workflow-id search. */
export function filterN8nOptions(options: N8nWorkflowOption[], query: string): N8nWorkflowOption[] {
  const normalized = query.trim().toLowerCase();
  return normalized === ''
    ? options
    : options.filter(
        (option) =>
          option.name.toLowerCase().includes(normalized) || option.workflowId.toLowerCase().includes(normalized),
      );
}

/** Parent-level catalog skills -> pickable options, hiding any already on the board. Nested skills
 * are dropped entirely — their runs roll up to the parent card, so only the parent is ever pickable.
 * Sourced from the skill catalog (not node labels), so harvested and imported skills all appear,
 * and the card id follows the run-join convention hermes-<skillId>. */
export function hermesSkillOptions(skills: SkillSummaryView[], existingCardIds: Set<string>): HermesSkillOption[] {
  return skills
    .filter((skill) => skill.parentSkillId === null)
    .map((skill) => {
      const cardId = sanitizeAgentId(`hermes-${skill.skillId}`);
      return {
        skillId: skill.skillId,
        name: skill.skillId,
        origin: skill.originNodeId ?? 'admin',
        version: skill.latestVersion,
        description: skill.description ?? null,
        cardId,
        onBoard: existingCardIds.has(cardId),
      };
    });
}

/**
 * The skills a loop may still be connected to. A card another loop already became is left out: two loops
 * on one card would each claim its runs, and unlinking either would read as breaking the other. The loop
 * being connected keeps its own card in the list so its current choice is never silently missing.
 */
export function connectableHermesOptions(
  options: HermesSkillOption[],
  loops: LoopView[],
  connectingLoopId?: string,
): HermesSkillOption[] {
  const claimed = new Set(
    loops
      .filter((loop) => loop.loopId !== connectingLoopId && loop.agentId !== null)
      .map((loop) => loop.agentId as string),
  );
  return options.filter((option) => !claimed.has(option.cardId));
}

/** The origins (nodes or 'admin') that own at least one addable skill, each once, in first-seen order. */
export function hermesFleets(options: HermesSkillOption[]): string[] {
  return [...new Set(options.map((option) => option.origin))];
}

/** Narrow Hermes skills to one fleet (empty = all) and a case-insensitive name/skill search. */
export function filterHermesOptions(options: HermesSkillOption[], fleetId: string, query: string): HermesSkillOption[] {
  const q = query.trim().toLowerCase();
  return options.filter(
    (option) =>
      (fleetId === '' || option.origin === fleetId) &&
      (q === '' || option.name.toLowerCase().includes(q) || option.skillId.toLowerCase().includes(q)),
  );
}

/** Build the request that validates a Hermes skill version and adds its read-only monitoring card. */
export function managedHermesCreateInput(option: HermesSkillOption, sectionId: string): CreateManagedHermesAgentInput {
  const input: CreateManagedHermesAgentInput = {
    sectionId,
    skillId: option.skillId,
    version: option.version,
  };
  if (option.description) {
    input.description = option.description.slice(0, TEXT_MAX);
  }
  return input;
}

/** Build the request that validates a live n8n workflow and adds only its monitoring card. */
export function managedN8nCreateInput(option: N8nWorkflowOption, sectionId: string): CreateManagedN8nAgentInput {
  return { sectionId, workflowId: option.workflowId };
}
